<!-- Copyright 2023, Common Good Learning Tools LLC -->
<template>
	<k-dialog v-model="dialog_open" fullscreen persistent scrollable transition="false">
		<k-card>
			<k-card-title style="border-bottom:1px solid #999"><k-icon large color="primary" class="mr-2">fas fa-chart-scatter</k-icon>
				<b>Cross-State Comparisons</b>
				<k-spacer></k-spacer>
				<k-btn color="primary" @click="$emit('dialog_cancel')">Done</k-btn>
			</k-card-title>
			<k-card-text class="ma-0 py-0 px-3" style="font-size:16px">
				<div class="d-flex">
					<!-- explanation -->
					<div class="k-csc-explanation-wrapper">
						<div class="float-right mt-1 mr-3">
							<k-select class="ml-2" style="width:240px" label="Choose a subject" outlined hide-details dense v-model="chosen_subject" :items="subject_select_options"></k-select>
						</div>
						<div class="mb-3 mt-2" v-html="chart_description"></div>
						<div v-if="chosen_subject!='all'">
							<div v-for="(region) in regions" :key="region.rname" class="d-flex my-3" v-if="region.rname!='National'">
								<b class="mr-4" style="width:132px; text-align:right;">{{ region.rname }}</b>
								<div v-for="(cat) in region.cats" :key="cat"><k-hover v-slot:default="{hover}">
									<k-btn small :color="region.color" class="mx-1" :class="btn_class(cat, hover)" @click="btn_clicked(cat)"><span style="color:#fff;">{{ cat }}</span></k-btn>
								</k-hover></div>
							</div>
						</div>
						<div class="mb-3 pa-3" style="border-top:1px solid #ccc;" v-html="chart_explanation"></div>
					</div>

					<!-- PLOT/DATA -->
					<div class="k-csc-chart-container" v-if="scatterplot_chart_options">
						<!-- if we're viewing a single category, show a header here and a btn to return to all states -->
						<div v-if="category_clicked" class="d-flex mb-5 mt-3">
							<div style="font-size:20px">Similarity scores with <b>{{ category_clicked }}</b>:</div>
							<k-spacer/>
							<k-btn small color="primary" class="k-tight-btn" @click="btn_clicked('')"><k-icon small class="mr-2">fas fa-undo</k-icon> Show all States</k-btn>
						</div>
						<highcharts ref="scatterplot_chart" class="chart" :key="scatterplot_key" :options="scatterplot_chart_options"></highcharts>

						<div v-if="category_clicked" class="k-csc-table-wrapper">
							<v-data-table light dense
								:headers="table_headers"
								disable-pagination
								hide-default-footer
								:items="table_rows"
								:sort-by="['sim_score']"
								:sort-desc="[true]"
							>
								<template v-slot:item="{ item }"><k-hover v-slot:default="{hover}"><tr :data-table-cat="item.cat" :style="category_hovered==item.cat ? 'font-weight:bold' : ''">
									<td>
										<span :style="`color:${item.region.color}`">{{ item.region.rname }}</span>
									</td>
									<td class="text-center">
										<span style="margin-left:-10px">{{ item.cat }}</span>
									</td>
									<td class="text-center">
										<span style="margin-left:-10px">{{ item.sim_score }}</span>
									</td>
								</tr></k-hover></template>
							</v-data-table>
						</div>
					</div>

				</div>
			</k-card-text>
		</k-card>
	</k-dialog>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import SubjectCompsMixin from './SubjectCompsMixin'

export default {
	mixins: [SubjectCompsMixin],
	props: {
		// req: { type: String, required: true },
		// nreq: { type: String, required: false, default() { return ''} },
	},
	data() { return {
		dialog_open: true,
		category_hovered: '',
		category_clicked: '',
		scatterplot_chart_options: null,
		scatterplot_key: 1,

		table_headers: [
			{ text: 'Region', align: 'left', sortable: true, value:'region' },
			{ text: 'State', align: 'center', sortable: true, value:'cat' },
			{ text: 'Avg. Sim. Score', align: 'center', sortable: true, value:'sim_score' },
		],
		table_rows: [],
	}},
	computed: {
		...mapState([]),
		...mapGetters([]),
		chosen_subject: {
			get() { return this.$store.state.lst.subject_comps_chosen_subject },
			set(val) { 
				this.$store.commit('lst_set', ['subject_comps_chosen_subject', val]) 
				if (val == 'all') this.category_clicked = ''
				this.create_chart_options()
			}
		},
		subject_select_options() {
			let arr = []
			for (let s in this.subject_data) {
				arr.push({value:s, text: this.subject_data[s].label})
			}
			return arr
		},
		chart_description() {
			let s = ''

			if (true) {
				s += `<h2 class="mb-5 mt-1">${this.subject_data[this.chosen_subject].label}</h2>`
				s += `<div class="mb-4" style="line-height:22px">${this.subject_data[this.chosen_subject].description}</div>`

				s += `<div class="mb-4"><b>To explore this data further:</b><ul>`
				s += `<li>Click and drag to zoom in on any area of the plot.</li>`
				if (this.chosen_subject != 'all') s += `<li>Click a state to see the exact calculated similarity with all other states.</li>`
				s += `</ul>`
			}

			return s
		},
		chart_explanation() {
			let s = ''
			s += `<div class="mb-4"><b>Calculation method:</b><ul style="font-size:12px;line-height:16px;" >`
			s += `<li>For every competency in every state’s (plus DC) framework, the open-source <a href="https://www.kaggle.com/models/google/universal-sentence-encoder" target="_blank">Universal Sentence Encoder</a> model is used to calculate a 512-dimensional vector representing the semantic meaning of the competency. For the purposes of this analysis, only competencies that are “leafs” in the framework’s hierarchical tree, as well as direct parents of those leafs, are considered.</li>`
			s += `<li>For every pair of states A and B, each competency in A is compared to every competency in B (limiting to competency pairs that both apply to the same grade level, or at most one grade level difference), by calculating the <a href="https://en.wikipedia.org/wiki/Cosine_similarity" target="_blank">cosine similarity</a> of the two competencies’ vectors and scaling to an integer “similarity score” value between 0 and 1000. </li>`
			s += `<li>The highest similarity scores for all competencies in framework A are averaged; then the highest similarity scores for all competencies in framework B are averaged; then these two averages are averaged to create the overall average similarity score between the two frameworks.</li>`
			s += `<li>To visualize all 1275 pairings in one scatterplot, a <a href="https://en.wikipedia.org/wiki/Multidimensional_scaling" target="_blank">multidimensional scaling</a> algorithm was used to reduce the 1275 distance scores to X, Y coordinates in an abstract Cartesian space. Note that the X and Y axes do not “mean” anything in the scatterplot; the MDS anaysis constructs factors that place each state on the plot in such a way that the distance between any two points reflects, as closely as possible, the similarity score between the two states—given the constraint that we are mapping a 51-dimensional dataset to 2 dimensions.</li>`
			if (this.chosen_subject != 'all') s += `<li>To see the exact similarity scores between pairs of states, click on one of the states above or on the plot.</li>`
			s += `</ul></div>`
			return s
		},
		// this doesn't work as a computed...
		// scatterplot_chart() { return this.$refs.scatterplot_chart.chart },
	},
	watch: {
	},
	created() {
		vapp.subject_comps_component = this
	},
	mounted() {
		setTimeout(x=>{
			this.create_chart_options()
		}, 10)
	},
	methods: {
		btn_class(cat, hover) {
			if (hover) {
				if (this.category_hovered != cat) {
					this.category_hovered = cat

					// if we're not already viewing a category, call this.create_chart_options to update the scatterplot
					if (true || !this.category_clicked) this.create_chart_options()
					// else scroll to this category in the table
					if (this.category_clicked) this.scroll_to_category()
					this.suppress_nohover = true	// note that we need this to *not* be reactive
					setTimeout(x=>this.suppress_nohover = false, 0)
				}
				return 'k-csc-cat-label k-csc-cat-label-hovered'
			} else {
				if (this.suppress_nohover === false) {
					this.category_hovered = ''
					if (true || !this.category_clicked) this.create_chart_options()
					this.suppress_nohover = null
				}
				return 'k-csc-cat-label'
			}
		},

		scroll_to_category() {
			let jq = $(`[data-table-cat=${this.category_hovered}]`)
			if (jq.length > 0) this.$vuetify.goTo(jq[0], {container:'.k-csc-table-wrapper', offset:0, duration:100})
		},

		btn_clicked(cat) {
			++this.scatterplot_key
			this.category_clicked = cat
			this.create_chart_options()
		},

		create_chart_options() {
			// calculate chart size
			let w = this.$vuetify.breakpoint.width - 40
			let h = this.$vuetify.breakpoint.height - 68 - 20
			let chart_size = (w > h) ? h : w
			if (chart_size > (this.$vuetify.breakpoint.width - 660)) {
				chart_size = (this.$vuetify.breakpoint.width - 660)
			}

			// scatterplot of mds for a subject
			if (empty(this.category_clicked)) {
				let data = this.scatterplot_data[this.chosen_subject]

				let arr
				// for 'all' subject, color by all_cats; otherwise use regions
				if (this.chosen_subject == 'all') arr = this.all_cats
				else arr = this.regions

				let hovered_index = null
				for (let i = 0; i < data.length; ++i) {
					let row = data[i]
					let reg = arr.find(x=>x.cats.includes(row.name))
					if (!reg) reg = arr.find(x=>x.cats.includes(row.name.substr(0,2)))
					if (reg) {
						row.color = reg.color

						if (this.chosen_subject == 'all') {
							row.marker = {radius: 16, lineWidth:0, }
							if (reg.rname != 'National' && !row.name.includes('GA')) row.dataLabels = { enabled: false }
							else row.dataLabels = { enabled: true }
						} else if (this.category_hovered == row.name) {
							row.marker = {radius: 24, lineWidth:1, lineColor:'black'}
							row.dataLabels = { enabled: true }
							hovered_index = i
						} else {
							row.marker = {radius: 16, lineWidth:0, }
							if (!empty(this.category_hovered)) row.dataLabels = { enabled: false }
							else row.dataLabels = { enabled: true }
						}
					}
				}
				if (!empty(hovered_index)) {
					// if we have a hovered point, move it to the front once things are rendered
					setTimeout(() => {
						let point = this.$refs.scatterplot_chart.chart.series[0].data[hovered_index]
						point.graphic?.toFront?.()
						// unfortunately the label doesn't come with it
					}, 50)
				}

				let plotlines = null
				if (this.chosen_subject != 'all') {
					plotlines = [{
						color: '#999',
						width: 1,
						value: 0,
						// zIndex: 5
					}]
				}

				let o = {
					chart: {
						type: 'scatter',
						width: chart_size,     // Width in pixels
						height: chart_size,     // Height in pixels
						plotBorderWidth: 0,
						zoomType: 'xy',
						dark: false,
						animation: false,
					},				
					title: { 
						text: undefined,
					},
					xAxis: {
						labels: {enabled: false},
						title: { text: null },
						lineWidth: 0,
						tickLength: 0,
						gridLineWidth: 0,
						plotLines: plotlines,
						minPadding: 0.05,
						maxPadding: 0.05,
					},
					yAxis: {
						labels: {enabled: false},
						title: { text: null },
						lineWidth: 0,
						tickLength: 0,
						gridLineWidth: 0,
						plotLines: plotlines,
						minPadding: 0.05,
						maxPadding: 0.05,
					},
					tooltip: {
						formatter: function() {
							// console.log(this)
							return `${this.key}`
							// return `${this.key}: ${this.x.toFixed(2)}, ${this.y.toFixed(2)}`
						}
					},
					plotOptions: {
						series: {
							animation: false,
							dataLabels: {
								enabled: true,
								format: '{point.name}',
								align: 'center',
								verticalAlign: 'middle',
								style: {
									fontSize: '12px',
									color: '#000000'
								}
							},
							point: {
								events: {
									click: function() {
										if (vapp.subject_comps_component.chosen_subject != 'all') {
											vapp.subject_comps_component.btn_clicked(this.name)
										}
									}
								}
							},
						}
					},
					legend: {
						enabled: false,
					},
					credits: { enabled: false },
					series: [{
						data: data,
						marker: {
							// radius: 16   // Radius in pixels; this affects the size of the circle
						}
					}]
				}

				this.scatterplot_chart_options = o

			////////////////////////
			// "number line" for a single state
			} else {
				let raw = this.distance_data[this.chosen_subject]

				// parse raw data
				let distances = {}, cats = [], other_data = {}
				let rows = raw.split('\n')
				let min = 10000, max = -1
				for (let i = 1; i < (rows.length-1); ++i) {		// first and last lines will be blank
					let cols = rows[i].split('\t')

					// second line will have cats; first and last col will be empty
					if (i == 1) {
						for (let j = 1; j < cols.length-1; ++j) {
							let cat = cols[j]
							cats.push(cat)
							distances[cat] = {}
							other_data[cat] = {}
						}
						continue
					}

					// subsequent rows will have a cat, followed by tabs until we start getting data, followed by the data, with an extra tab at the end
					let row_cat = cols[0]

					// except the last 3 rows before the end, which are...
					if (row_cat == '#items') {
						other_data.item_count = {}
						for (let j = 1; j < cols.length-1; ++j) {
							let col_cat = cats[j-1]
							other_data.item_count[col_cat] = cols[j] * 1
						}

					} else if (row_cat == '#el_pct') {
						other_data.el_pct = {}
						for (let j = 1; j < cols.length-1; ++j) {
							let col_cat = cats[j-1]
							other_data.el_pct[col_cat] = cols[j] * 1
						}

					} else if (row_cat == '#avg_sim') {
						other_data.avg_sim = {}
						for (let j = 1; j < cols.length-1; ++j) {
							let col_cat = cats[j-1]
							other_data.avg_sim[col_cat] = cols[j] * 1
						}
						
					} else {
						for (let j = 1; j < cols.length-1; ++j) {
							let cell = cols[j].trim()
							if (empty(cell)) continue
							else cell = cell * 1

							let col_cat = cats[j-1]

							// insert this val into both the row and col cats' objects
							distances[col_cat][row_cat] = cell
							distances[row_cat][col_cat] = cell

							if (cell < min) min = cell
							if (cell > max) max = cell
							// if (cell > max) {max = cell; console.log(`max: ${col_cat}-${row_cat} = ${max}`) }
						}
					}
				}

				// console.warn(`${this.chosen_subject}:: min: ${min} / max: ${max}`)

				// now calculate the data for the chosen state; create table_rows as we go
				let data = []
				let table_rows = []
				let i = 0
				let hovered_index = null
				for (let cat of cats) {
					let reg = this.regions.find(x=>x.cats.includes(cat))
					// skip items that aren't in regions (e.g. "old" versions)
					if (empty(reg)) {
						continue
					}

					let marker, dataLabels
					let x

					let sim_score = distances[this.category_clicked][cat]

					if (cat == this.category_clicked) {
						x = 0
						marker = {radius:16}
						dataLabels = { enabled: true }

					} else {
						x = 1000 - sim_score

						if (this.category_hovered == cat) {
							marker = {radius: 16, lineWidth:1, lineColor:'black'}
							dataLabels = { enabled: true }
							hovered_index = i
						} else {
							marker = {radius: 8, lineWidth:0, }
							if (!empty(this.category_hovered)) dataLabels = { enabled: false }
							else dataLabels = { enabled: true }
						}

						// we don't push a row for the chosen category
						table_rows.push({
							region: reg,
							cat: cat,
							sim_score: sim_score
						})
					}

					let d = {
						name: cat,
						x: x,
						y: 0,
						marker: marker,
						dataLabels: dataLabels,
						color: reg.color,
						category_clicked: this.category_clicked,
					}

					data.push(d)
					++i
				}

				// if (!empty(hovered_index)) {
				// 	// if we have a hovered point, move it to the front once things are rendered (unfortunately this doesn't seem to work here...)
				// 	setTimeout(() => {
				// 		let point = this.$refs.scatterplot_chart.chart.series[0].data[hovered_index]
				// 		point.graphic?.toFront?.()
				// 	}, 50);
				// }

				// sort rows and save to this.data
				// table_rows.sort(x=>)
				this.table_rows = table_rows
				
				let o = {
					chart: {
						type: 'scatter',
						width: chart_size,     // Width in pixels
						height: 100,
						plotBorderWidth: 0,
						zoomType: 'xy',
						dark: false,
						animation: false,
					},
					title: {
						text: undefined,
					},
					xAxis: {
						title: { text: null },
						// gridLineWidth: 1,
						// lineWidth: 2,
						// tickInterval: 1
						lineWidth: 0,
						tickLength: 0,
						gridLineWidth: 0,
						plotLines: [{
							color: '#000000',
							width: 1,
							value: 0,
							// zIndex: 5
						}],
						minPadding: 0.05,
						maxPadding: 0.05,
					},
					yAxis: {
						min: -1,
						max: 1,
						title: { text: null },
						labels: { enabled: false },
						lineWidth: 0,
						tickLength: 0,
						gridLineWidth: 0,
						plotLines: [{
							color: '#000000',
							width: 1,
							value: 0,
							// zIndex: 5
						}],
					},
					tooltip: {
						formatter: function() {
							// console.log(this)
							return `${this.point?.category_clicked} <-> ${this.key}: avg. similarity score ${1000 - this.x}`
							// return `${this.key}: ${this.x.toFixed(2)}, ${this.y.toFixed(2)}`
						}
					},
					plotOptions: {
						series: {
							animation: false,
							dataLabels: {
								enabled: true,
								format: '{point.name}',
								align: 'center',
								verticalAlign: 'middle',
								style: {
									fontSize: '12px',
									color: '#000000'
								}
							},
							point: {
								events: {
									click: function() {
										vapp.subject_comps_component.btn_clicked(this.name)
									}
								}
							},
						}
					},
					legend: {
						enabled: false,
					},
					credits: { enabled: false },
					series: [{
						data: data,
						// marker: {
						// 	radius: 16   // Radius in pixels; this affects the size of the circle
						// }
					}]
				}

				this.scatterplot_chart_options = o
			}
		}
	}
}
</script>

<style lang="scss">
.k-csc-chart-container {
	// width:600px;
	// height:600px;
	// margin:auto;
	// position:relative;	
	// z-index: 1;
	// max-width:calc(100vw - 860px);
}

.k-csc-cat-label {
	color: white;
	width:56px!important;
	max-width:56px!important;
	text-shadow:
		-1px -1px 0 #000,  
		1px -1px 0 #000,
		-1px  1px 0 #000,
		1px  1px 0 #000;
}

.k-csc-cat-label-hovered {
	font-size:20px!important;
}

.k-csc-explanation-wrapper {
	margin-right:12px;
	padding-top:8px;
	max-height: calc(100vh - 72px);
	overflow:auto;
}
.k-csc-table-wrapper {
	max-height: calc(100vh - 236px);
	overflow:auto;
	border:1px solid #999;
}
</style>